import { Button } from '@qi/ui';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '../../lib/analytics.ts';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Minimal class boundary — TanStack Router's per-route boundary handles loader
 * errors; this one catches render-time throws further down the tree. Logs to
 * the console and reports to PostHog error tracking (consent-gated).
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('UI boundary caught', error, info);
    captureException(error, { componentStack: info.componentStack });
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-rose">
            Something fractured
          </p>
          <h1 className="font-display text-4xl text-ink-0">We caught a stray signal</h1>
          <p className="max-w-md text-ink-1">{this.state.error.message}</p>
          <Button variant="resonance" onClick={this.reset}>
            Reset and continue
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
